/**
 * useAgentUpdates - Hook for real-time AI agent status updates
 * Uses WebSocket with polling fallback for real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ClaimPipeline, AgentStatus, AI_AGENTS, PIPELINE_STAGES } from '../types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 10000; // 10 seconds (fallback if WebSocket fails)
const WS_RECONNECT_DELAY = 3000; // 3 seconds

interface UseAgentUpdatesOptions {
  pollInterval?: number;
  enableWebSocket?: boolean;
}

interface UseAgentUpdatesResult {
  pipeline: ClaimPipeline | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  connected: boolean;
}

export function useAgentUpdates(
  claimId?: string,
  options: UseAgentUpdatesOptions = {}
): UseAgentUpdatesResult {
  const { pollInterval = POLL_INTERVAL, enableWebSocket = true } = options;
  
  const [pipeline, setPipeline] = useState<ClaimPipeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Fetch pipeline status via REST API
  const fetchPipelineStatus = useCallback(async () => {
    try {
      const endpoint = claimId 
        ? `${BACKEND_URL}/api/v1/pipeline/status/${claimId}`
        : `${BACKEND_URL}/api/v1/pipeline/status`;
      
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        if (response.status === 404) {
          setPipeline(generateMockPipeline(claimId));
          setLoading(false);
          return;
        }
        throw new Error('Failed to fetch pipeline status');
      }
      
      const data = await response.json();
      setPipeline(data);
      setError(null);
    } catch (err) {
      console.warn('Pipeline status fetch failed, using mock data:', err);
      setPipeline(generateMockPipeline(claimId));
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [claimId]);
  
  const refresh = useCallback(() => {
    setLoading(true);
    fetchPipelineStatus();
  }, [fetchPipelineStatus]);
  
  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!enableWebSocket || !BACKEND_URL) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    try {
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const wsEndpoint = claimId 
        ? `${wsUrl}/api/v1/ws/pipeline/${claimId}`
        : `${wsUrl}/api/v1/ws/pipeline`;
      
      console.log('Connecting to pipeline WebSocket:', wsEndpoint);
      wsRef.current = new WebSocket(wsEndpoint);
      
      wsRef.current.onopen = () => {
        console.log('✅ Pipeline WebSocket connected');
        setConnected(true);
        setError(null);
        
        // Stop polling when WebSocket is connected
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'initial_status' || data.type === 'refresh') {
            if (data.pipeline) {
              setPipeline(data.pipeline);
              setLoading(false);
            }
          } else if (data.type?.startsWith('agent_')) {
            // Update specific agent in pipeline
            setPipeline(prev => {
              if (!prev) return prev;
              
              const updatedAgents = prev.agents.map(agent => {
                if (agent.agentId === data.agentId) {
                  return {
                    ...agent,
                    status: data.type === 'agent_started' ? 'running' :
                            data.type === 'agent_complete' ? 'complete' :
                            data.type === 'agent_error' ? 'error' :
                            data.type === 'agent_progress' ? 'running' :
                            agent.status,
                    progress: data.current && data.total ? {
                      current: data.current,
                      total: data.total,
                      percentage: data.percentage || Math.round((data.current / data.total) * 100)
                    } : agent.progress,
                    result: data.summary ? { summary: data.summary } : agent.result
                  };
                }
                return agent;
              });
              
              return {
                ...prev,
                agents: updatedAgents,
                currentAgent: data.type === 'agent_started' ? data.agentId : 
                              data.type === 'agent_complete' ? undefined : 
                              prev.currentAgent,
                lastUpdated: new Date().toISOString()
              };
            });
          }
        } catch (err) {
          console.warn('Failed to parse WebSocket message:', err);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected, will reconnect...');
        setConnected(false);
        
        // Reconnect after delay
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, WS_RECONNECT_DELAY);
        
        // Start polling as fallback
        if (!pollIntervalRef.current) {
          pollIntervalRef.current = setInterval(fetchPipelineStatus, pollInterval);
        }
      };
      
      wsRef.current.onerror = (err) => {
        console.warn('WebSocket error:', err);
        setConnected(false);
      };
      
    } catch (err) {
      console.warn('WebSocket connection failed:', err);
      setConnected(false);
      
      // Fall back to polling
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(fetchPipelineStatus, pollInterval);
      }
    }
  }, [claimId, enableWebSocket, fetchPipelineStatus, pollInterval]);
  
  // Initial fetch and connection setup
  useEffect(() => {
    fetchPipelineStatus();
    
    if (enableWebSocket) {
      connectWebSocket();
    } else {
      // Use polling if WebSocket disabled
      pollIntervalRef.current = setInterval(fetchPipelineStatus, pollInterval);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchPipelineStatus, connectWebSocket, enableWebSocket, pollInterval]);
  
  return { pipeline, loading, error, refresh, connected };
}

// Generate mock pipeline data for demo/development
function generateMockPipeline(claimId?: string): ClaimPipeline {
  const now = new Date();
  
  // Simulate different states based on claimId or random
  const seed = claimId ? claimId.charCodeAt(0) : Math.random() * 100;
  const progressPercent = Math.floor((seed % 100));
  
  const agents: AgentStatus[] = PIPELINE_STAGES.map((agentId, index) => {
    const agentDef = AI_AGENTS[agentId];
    let status: AgentStatus['status'] = 'queued';
    let progress: AgentStatus['progress'] | undefined;
    let result: AgentStatus['result'] | undefined;
    
    // Determine status based on progress
    const stageProgress = (index / PIPELINE_STAGES.length) * 100;
    
    if (stageProgress < progressPercent - 20) {
      status = 'complete';
      result = {
        summary: `Processed successfully`,
        itemsNeedingReview: index === 1 ? Math.floor(Math.random() * 10) : 0
      };
    } else if (stageProgress < progressPercent) {
      status = 'running';
      progress = {
        current: Math.floor(Math.random() * 100) + 50,
        total: 150,
        percentage: Math.floor(Math.random() * 40) + 50
      };
    } else {
      status = 'queued';
    }
    
    return {
      ...agentDef,
      status,
      progress,
      result,
      startedAt: status !== 'queued' ? new Date(now.getTime() - Math.random() * 600000).toISOString() : undefined,
      completedAt: status === 'complete' ? new Date(now.getTime() - Math.random() * 300000).toISOString() : undefined,
    } as AgentStatus;
  });
  
  const currentAgent = agents.find(a => a.status === 'running')?.agentId;
  const pendingReview = agents
    .filter(a => a.result?.itemsNeedingReview)
    .reduce((sum, a) => sum + (a.result?.itemsNeedingReview || 0), 0);
  
  return {
    claimId: claimId || 'all',
    claimName: claimId ? `Claim ${claimId.slice(0, 8)}` : 'All Claims',
    agents,
    currentAgent,
    pendingHumanReview: pendingReview,
    estimatedCompletion: new Date(now.getTime() + 300000).toISOString(),
    lastUpdated: now.toISOString()
  };
}

export default useAgentUpdates;
